import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import axios from 'axios';
import { Box, Typography, CircularProgress, LinearProgress, Chip, useMediaQuery, useTheme } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const UsageSummary = () => {
  const [usedWords, setUsedWords] = useState(0);
  const [limitWords, setLimitWords] = useState(null);
  const [period, setPeriod] = useState('day');
  const [tier, setTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Responsive design hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  
  // Function to get or create session ID
  const getSessionId = () => {
    const newKey = 'lexory_session_id';
    const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';
    let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
    if (!sessionId) {
      sessionId = uuidv4();
    }
    localStorage.setItem(newKey, sessionId);
    return sessionId;
  };
  
  useEffect(() => {
    const fetchUsageStats = async () => {
      setLoading(true);
      
      try {
        const sessionId = getSessionId();

        const { data, error: sessionError } = await supabase.auth.getSession();
        const currentUser = sessionError ? null : data?.session?.user || null;
        setUser(currentUser);

        const token = sessionError ? null : data?.session?.access_token;
        const headers = {
          'X-Session-Id': sessionId
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get('/api/usage', { headers });
        const api = response?.data;

        setTier(api?.tier || 'free');
        setPeriod(api?.period || 'day');
        setUsedWords(api?.usedWords || 0);
        setLimitWords(typeof api?.limitWords === 'number' ? api.limitWords : null);
        
      } catch (err) {
        console.error('Unexpected error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsageStats();
    
    // Refresh stats every minute
    const interval = setInterval(fetchUsageStats, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const usagePercentage = typeof limitWords === 'number' && limitWords > 0
    ? Math.min(100, (usedWords / limitWords) * 100)
    : 0;
  
  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }
  return (
    <Box sx={{ 
      mt: 2, 
      mb: 4, 
      p: isMobile ? 1.5 : 2, 
      bgcolor: 'background.paper', 
      borderRadius: 1, 
      boxShadow: 1 
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'center' : 'space-between', 
        alignItems: isMobile ? 'center' : 'center', 
        mb: isMobile ? 2 : 1,
        gap: isMobile ? 1 : 0
      }}>
        <Typography 
          variant={isSmallMobile ? "subtitle1" : "h6"} 
          gutterBottom={!isMobile}
          sx={{ 
            textAlign: isMobile ? 'center' : 'left',
            mb: isMobile ? 0 : undefined
          }}
        >
          Today’s Reading Stats
        </Typography>
        
        <Chip 
          label={user ? `👤 ${user.email}` : '🌐 Anonymous'}
          color={user ? 'primary' : 'default'}
          size={isSmallMobile ? "small" : "medium"}
          variant="outlined"
          sx={{
            fontSize: isSmallMobile ? '0.75rem' : undefined,
            maxWidth: isMobile ? '100%' : undefined
          }}
        />
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Typography 
            variant={isSmallMobile ? "caption" : "body2"} 
            color="text.secondary" 
            gutterBottom
            sx={{ 
              textAlign: isMobile ? 'center' : 'left',
              fontSize: isSmallMobile ? '0.75rem' : undefined
            }}
          >
            {typeof limitWords === 'number' && limitWords > 0
              ? `${period === 'month' ? 'This month' : 'Today'}: ${usedWords} / ${limitWords} words (${tier})`
              : `${period === 'month' ? 'This month' : 'Today'}: ${usedWords} words (${tier})`}
          </Typography>

          {typeof limitWords === 'number' && limitWords > 0 ? (
            <>
              <LinearProgress 
                variant="determinate" 
                value={usagePercentage} 
                sx={{ 
                  height: isMobile ? 8 : 10, 
                  borderRadius: 5,
                  mb: isMobile ? 1.5 : 1,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: usagePercentage > 90 ? 'error.main' : 
                             usagePercentage > 70 ? 'warning.main' : 'success.main'
                  }
                }}
              />
              <Typography 
                variant={isSmallMobile ? "caption" : "body2"} 
                color="text.secondary"
                sx={{ 
                  textAlign: isMobile ? 'center' : 'left',
                  fontSize: isSmallMobile ? '0.75rem' : undefined,
                  mb: user && isMobile ? 1 : 0
                }}
              >
                Remaining: {Math.max(0, limitWords - usedWords)} words
              </Typography>
            </>
          ) : (
            <Typography 
              variant={isSmallMobile ? "caption" : "body2"} 
              color="text.secondary"
              sx={{ 
                textAlign: isMobile ? 'center' : 'left',
                fontSize: isSmallMobile ? '0.75rem' : undefined,
                mb: user && isMobile ? 1 : 0
              }}
            >
              Unlimited
            </Typography>
          )}
          
          {user && (
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ 
                display: 'block', 
                mt: isMobile ? 0 : 1,
                textAlign: isMobile ? 'center' : 'left',
                fontSize: isSmallMobile ? '0.7rem' : undefined
              }}
            >
              Tracking usage for your account
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default UsageSummary;
